<template>
  <div ref="chartContainer" class="combined-chart-container">
    <div v-if="loading" class="chart-loading">
      <slot name="loading">
        <b-spinner label="Loading..." variant="primary"></b-spinner>
      </slot>
    </div>
    <div v-if="!loading && datasets.length === 0" class="no-data">
      <slot name="no-data">No data available</slot>
    </div>
    <div :id="chartId" class="chart-wrapper"></div>
    <div ref="tooltip" class="d3-tooltip"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import moment from 'moment';

export default {
  name: 'CombinedChart',
  props: {
    chartId: {
      type: String,
      default: 'combined-chart',
    },
    data: {
      type: Array,
      default: () => [],
    },
    datasets: {
      type: Array,
      default: () => [],
    },
    timestampKey: {
      type: String,
      default: 'timestamp',
    },
    xFormat: {
      type: String,
      default: '%d %b %H:%M',
    },
    height: {
      type: Number,
      default: 300,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    // Left Y-axis configuration
    leftYLabel: {
      type: String,
      default: '',
    },
    leftYTickFormat: {
      type: String,
      default: '',
    },
    // Right Y-axis configuration
    rightYLabel: {
      type: String,
      default: '',
    },
    rightYTickFormat: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      chart: null,
      seriesVisibility: {},
    };
  },
  watch: {
    datasets: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
    data: {
      handler() {
        this.drawChart();
      },
      deep: true,
    },
    loading() {
      if (!this.loading) {
        this.drawChart();
      }
    },
  },
  mounted() {
    if (!this.loading) {
      this.drawChart();
    }
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    drawChart() {
      if (!this.$refs.chartContainer || this.datasets.length === 0) return;

      const container = this.$refs.chartContainer;
      const tooltip = this.$refs.tooltip;
      d3.select(`#${this.chartId}`).selectAll('*').remove();
      d3.select(tooltip).selectAll('*').remove();

      const margin = {
        top: 20, right: 120, bottom: 50, left: 60,
      };
      const width = container.clientWidth - margin.left - margin.right;
      const height = this.height - margin.top - margin.bottom;

      const svg = d3.select(`#${this.chartId}`)
        .append('svg')
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Extract timestamps
      const timestamps = this.data.map((d) => new Date(d[this.timestampKey] * 1000));

      // X scale (time)
      const x = d3.scaleTime()
        .domain(d3.extent(timestamps))
        .range([0, width]);

      // Find datasets by axis type
      const lineDatasets = this.datasets.filter((ds) => ds.type !== 'bar');
      const barDatasets = this.datasets.filter((ds) => ds.type === 'bar');

      // Y scale for line (left)
      const lineValues = lineDatasets.flatMap((ds) => this.data.map((d) => d[ds.dataKey]).filter((v) => v !== null && v !== undefined));
      const maxLineValue = d3.max(lineValues) || 0;
      const yLine = d3.scaleLinear()
        .domain([0, Math.ceil(maxLineValue / 5) * 5 + 5])
        .range([height, 0]);

      // Y scale for bars (right)
      let yBar;
      if (barDatasets.length > 0) {
        const barValues = barDatasets.flatMap((ds) => this.data.map((d) => d[ds.dataKey]).filter((v) => v !== null && v !== undefined));
        const maxBarValue = d3.max(barValues) || 1;
        yBar = d3.scaleLinear()
          .domain([0, (Math.ceil(maxBarValue * 10) / 10) + 0.5])
          .range([height, 0]);
      }

      // Add X axis
      svg.append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).tickFormat(d3.timeFormat(this.xFormat)))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end')
        .attr('dx', '-0.5em')
        .attr('dy', '0.5em');

      // Add left Y axis (line)
      const leftYFormat = this.leftYTickFormat || ((d) => d);
      svg.append('g')
        .call(d3.axisLeft(yLine).tickFormat(leftYFormat));

      // Left Y axis label
      if (this.leftYLabel) {
        svg.append('text')
          .attr('transform', 'rotate(-90)')
          .attr('y', -45)
          .attr('x', -height / 2)
          .attr('text-anchor', 'middle')
          .attr('fill', lineDatasets[0]?.color || '#0d6efd')
          .attr('font-size', '12px')
          .text(this.leftYLabel);
      }

      // Add right Y axis (bar)
      if (yBar) {
        const rightYFormat = this.rightYTickFormat || ((d) => d);
        svg.append('g')
          .attr('transform', `translate(${width},0)`)
          .call(d3.axisRight(yBar).tickFormat(rightYFormat));

        // Right Y axis label - positioned correctly
        if (this.rightYLabel) {
          svg.append('text')
            .attr('transform', 'rotate(90)')
            .attr('y', width + 65)
            .attr('x', height / 2)
            .attr('dy', '.5em')
            .attr('text-anchor', 'middle')
            .attr('fill', barDatasets[0]?.color || '#0dcaf0')
            .attr('font-size', '12px')
            .text(this.rightYLabel);
        }
      }

      // Grid lines
      svg.append('g')
        .attr('class', 'grid')
        .attr('opacity', 0.1)
        .call(d3.axisLeft(yLine).tickSize(-width).tickFormat(''));

      // Initialize series visibility
      this.datasets.forEach((ds) => {
        this.seriesVisibility[ds.name] = true;
      });

      // Draw line datasets
      lineDatasets.forEach((series) => {
        const seriesData = this.data.map((d) => d[series.dataKey]);

        svg.append('path')
          .datum(seriesData)
          .attr('fill', 'none')
          .attr('stroke', series.color)
          .attr('stroke-width', 2.5)
          .attr('d', d3.line()
            .defined((d) => d !== null && d !== undefined)
            .x((d, i) => x(timestamps[i]))
            .y((d) => yLine(d)))
          .on('mouseover', () => {
            d3.select(this).attr('stroke-width', 4);
          })
          .on('mouseout', () => {
            d3.select(this).attr('stroke-width', 2.5);
          });

        // Dots
        svg.selectAll(`.dot-${series.name.replace(/\s+/g, '-')}`)
          .data(seriesData.map((d, i) => ({ value: d, date: timestamps[i] }))
            .filter((d) => d.value !== null && d.value !== undefined))
          .enter()
          .append('circle')
          .attr('class', `dot-${series.name.replace(/\s+/g, '-')}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yLine(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');
      });

      // Draw bar datasets
      if (yBar && barDatasets.length > 0) {
        const barWidth = (width / this.data.length) * 0.7;
        const barDataset = barDatasets[0]; // Support single bar dataset for now

        svg.selectAll('.bar')
          .data(this.data.filter((d) => d[barDataset.dataKey] !== null && d[barDataset.dataKey] !== undefined))
          .enter()
          .append('rect')
          .attr('class', 'bar')
          .attr('x', (d) => x(new Date(d[this.timestampKey] * 1000)) - barWidth / 2)
          .attr('y', (d) => yBar(d[barDataset.dataKey]))
          .attr('width', barWidth)
          .attr('height', (d) => height - yBar(d[barDataset.dataKey]))
          .attr('fill', `rgba(${this.hexToRgb(barDataset.color)}, 0.3)`)
          .attr('stroke', barDataset.color)
          .attr('stroke-width', 1);
      }

      // Focus group for hover
      const focusGroup = svg.append('g').style('display', 'none');
      const focusLine = focusGroup.append('line')
        .attr('stroke', '#aaa')
        .attr('stroke-dasharray', '3 3')
        .attr('y1', 0)
        .attr('y2', height);

      const focusCircles = lineDatasets.map((ds) => focusGroup.append('circle')
        .attr('r', 5)
        .attr('fill', ds.color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2));

      // Legend with click toggle
      const legendItemWidth = 100;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - Math.min(this.datasets.length, legendColumns) * legendItemWidth) / 2)}, -10)`);

      const legendItem = legendGroup.selectAll('g')
        .data(this.datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * 18})`;
        })
        .style('cursor', 'pointer');

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '11px')
        .text((d) => d.name);

      const updateLegend = () => {
        legendItem.select('rect').attr('opacity', (d) => (this.seriesVisibility[d.name] ? 1 : 0.3));
        legendItem.select('text')
          .attr('opacity', (d) => (this.seriesVisibility[d.name] ? 1 : 0.4))
          .style('text-decoration', (d) => (this.seriesVisibility[d.name] ? 'none' : 'line-through'));
      };

      legendItem.on('click', (event, d) => {
        this.seriesVisibility[d.name] = !this.seriesVisibility[d.name];
        updateLegend();
      });

      updateLegend();

      // Hover interaction
      const bisect = d3.bisector((d) => d).left;

      svg.append('rect')
        .attr('width', width)
        .attr('height', height)
        .style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => {
          d3.select(tooltip).style('opacity', 1);
          focusGroup.style('display', null);
        })
        .on('mouseout', () => {
          d3.select(tooltip).style('opacity', 0);
          focusGroup.style('display', 'none');
        })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          let index = 0;
          if (i < timestamps.length && i > 0) {
            index = (x0 - timestamps[i - 1] > timestamps[i] - x0) ? i : i - 1;
          }
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);

          lineDatasets.forEach((ds, j) => {
            if (!this.seriesVisibility[ds.name]) {
              focusCircles[j].style('display', 'none');
              return;
            }
            const val = this.data[index]?.[ds.dataKey];
            if (val !== null && val !== undefined) {
              focusCircles[j].attr('cx', xPos).attr('cy', yLine(val)).style('display', null);
            } else {
              focusCircles[j].style('display', 'none');
            }
          });

          let html = `<strong>${moment(date).format('DD MMM YYYY HH:mm Z')}</strong><br/>`;
          this.datasets.forEach((ds) => {
            if (!this.seriesVisibility[ds.name]) return;
            const val = this.data[index]?.[ds.dataKey];
            if (val !== null && val !== undefined) {
              html += `<div style="display:flex; align-items:center; gap:5px;">
                        <span style="width:10px; height:10px; border-radius:50%; background:${ds.color}; display:inline-block;"></span>
                        ${ds.name}: ${typeof val === 'number' ? val.toFixed(ds.type === 'bar' ? 2 : 1) : val}
                      </div>`;
            }
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          if (left + 160 > width) left = posX - 170;

          d3.select(tooltip)
            .html(html)
            .style('left', `${left}px`)
            .style('top', `${posY + 15}px`);
        });

      this.chart = svg;
    },
    destroyChart() {
      if (this.$refs.chartContainer) {
        d3.select(`#${this.chartId}`).selectAll('*').remove();
      }
      this.chart = null;
    },
    hexToRgb(hex) {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result
        ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
        : '200, 200, 200';
    },
  },
};
</script>

<style scoped>
.combined-chart-container {
  position: relative;
  width: 100%;
}

.chart-wrapper {
  width: 100%;
  min-height: 220px;
}

.chart-wrapper svg {
  max-width: 100%;
  overflow: visible;
}

.chart-wrapper .domain {
  stroke: #dee2e6;
  stroke-width: 1;
}

.chart-wrapper .tick line {
  stroke: #dee2e6;
}

.chart-wrapper .tick text {
  fill: #6c757d;
  font-size: 11px;
}

.chart-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.9);
  z-index: 10;
}

.no-data {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 220px;
  color: #6c757d;
  font-size: 0.875rem;
}

.d3-tooltip {
  position: absolute;
  background: rgba(0, 0, 0, 0.85);
  color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  z-index: 100;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  max-width: 200px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.d3-tooltip strong {
  display: block;
  margin-bottom: 6px;
  font-size: 11px;
  color: #e9ecef;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  padding-bottom: 4px;
}

.d3-tooltip > div {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 3px 0;
}

.chart-legend text {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  fill: #6c757d;
}

.chart-legend rect {
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.grid line {
  stroke: #e9ecef;
  stroke-dasharray: 2, 2;
}

path[stroke] {
  transition: stroke-width 0.15s ease;
}

.bar {
  transition: opacity 0.2s ease;
}

.bar:hover {
  opacity: 0.8;
}
</style>
